/**
 * Phase 6 Plan 02 — Wire-format types + TanStack Query key + fetcher functions
 * for /api/admin/cleaning-settings (PM-only GET + PATCH; Plan 01 Task 1).
 *
 * Mirrors server-side schemas (server/app/settings_domain/schemas.py):
 *   CleaningSettingsRead             ↔ CleaningSettingsRead
 *   CleaningSettingsPatchPayload     ↔ CleaningSettingsPatchIn (PATCH body)
 *   CleaningSettingsUpdatedResponse  ↔ 200 OK envelope
 *
 * The PATCH endpoint is in CREDENTIAL_PATHS (Phase 5 defense-in-depth precedent,
 * extended 11 → 14 in Plan 01 Task 1) so the FastAPI 422 redactor strips the
 * cross-field model_validator's discriminator string from the wire body. This
 * client maps mutation.error.code to the ERR_* Banner localization on the page;
 * the discriminator is locked at the Pydantic unit-test layer server-side.
 *
 * Per D-141 — useUpdateCleaningSettings (web/src/pages/settings/) invalidates
 * BOTH ASSETS_GEOJSON_KEY (network-wide map recolor) AND CLEANING_SETTINGS_KEY
 * (settings page) on Save success (multi-key pattern matching Plan 04-04
 * useInspectionEdit precedent).
 */
import { apiGet, apiPatch } from "./client";

/**
 * GET response — current cleaning_settings singleton row (id=1 enforced by DB
 * CHECK + Pydantic + service layer).
 */
export interface CleaningSettingsRead {
  /** Cadence between scheduled cleanings, in months (DB CHECK >= 1). */
  cadence_months: number;
  /** Pre-due warning window, in days (DB CHECK >= 0 AND < cadence_months × 30). */
  warning_days: number;
  /** ISO 8601 UTC; bumped server-side on every Save click (CP7 cursor-friendly). */
  updated_at: string;
}

/**
 * PATCH body — both fields mandatory (the singleton row already exists per
 * Alembic 0006 seed; PATCH is always a full update). Mirrors server's
 * CleaningSettingsPatchIn with `extra='forbid'`.
 */
export interface CleaningSettingsPatchPayload {
  cadence_months: number;
  warning_days: number;
}

/** 200 success envelope from the PATCH endpoint. */
export interface CleaningSettingsUpdatedResponse {
  code: "OK_CLEANING_SETTINGS_UPDATED";
  cadence_months: number;
  warning_days: number;
  updated_at: string;
}

/** TanStack Query key for the cleaning settings singleton. */
export const CLEANING_SETTINGS_KEY = ["cleaning-settings"] as const;

/** GET /api/admin/cleaning-settings (PM-only; pm_session cookie travels via apiGet). */
export const fetchCleaningSettings = (): Promise<CleaningSettingsRead> =>
  apiGet<CleaningSettingsRead>("/api/admin/cleaning-settings");

/** PATCH /api/admin/cleaning-settings (PM-only). */
export const patchCleaningSettings = (
  payload: CleaningSettingsPatchPayload,
): Promise<CleaningSettingsUpdatedResponse> =>
  apiPatch<CleaningSettingsUpdatedResponse>(
    "/api/admin/cleaning-settings",
    payload,
  );
